import { Scene, Color3, Color4, HemisphericLight } from '@babylonjs/core';
import { logger } from '../core/Logger';
import { MapConfig } from './LevelBuilder';
import { CameraConfig } from './camera/CameraConfig';

/**
 * Runtime environment settings. Always initialized fresh from map config or defaults.
 * Does not persist - resets on every map load.
 */
export class EnvironmentSettings {
    private scene: Scene;
    
    // Map-loaded environment properties (preserved)
    private mapAmbientColor: Color3;
    private defaultCameraConfig: CameraConfig | null = null;
    private fogConfig: { enabled: boolean; mode: 'linear' | 'exp' | 'exp2'; density: number; start: number; end: number; color: Color3 } | null = null;
    
    // Debug overrides
    private fullBrightMode: boolean = false;
    
    constructor(scene: Scene) {
        this.scene = scene;
        this.mapAmbientColor = Color3.Black();
        this.reset();
    }
    
    /**
     * Reset to default values
     */
    public reset(): void {
        this.mapAmbientColor = Color3.Black();
        this.defaultCameraConfig = null;
        this.fogConfig = null;
        this.fullBrightMode = false;
        // Disable fog on reset
        this.scene.fogMode = Scene.FOGMODE_NONE;
        logger.level.debug('🌍 Environment settings reset to defaults');
    }
    
    /**
     * Load settings from map config and apply to scene
     */
    public loadFromMapConfig(mapConfig: MapConfig): void {
        // Don't reset - only update map values, preserve debug state
        
        logger.environment.debug('loadFromMapConfig called, mapConfig:', mapConfig);
        logger.environment.debug('mapConfig.ambient:', mapConfig.ambient);
        logger.level.log(`loadFromMapConfig called, mapConfig.ambient:`, mapConfig.ambient);
        
        // Load ambient light color from map
        if (mapConfig.ambient) {
            this.mapAmbientColor = new Color3(
                mapConfig.ambient.x,
                mapConfig.ambient.y,
                mapConfig.ambient.z
            );
            logger.environment.debug('Map ambient color SET to:', this.mapAmbientColor.r, this.mapAmbientColor.g, this.mapAmbientColor.b);
            logger.level.log(`Map ambient color loaded: (${mapConfig.ambient.x}, ${mapConfig.ambient.y}, ${mapConfig.ambient.z})`);
        } else {
            this.mapAmbientColor = Color3.Black();
            logger.environment.debug('No ambient in map config, using black');
            logger.level.log(`🌍 No ambient in map config, using black`);
        }
        
        // Load camera configuration from map
        if (mapConfig.camera) {
            this.defaultCameraConfig = mapConfig.camera;
            logger.level.log(`📷 Default camera config loaded from map: type=${mapConfig.camera.type}`);
        } else {
            this.defaultCameraConfig = null;
            logger.level.debug(`📷 No camera config in map, using fallback`);
        }
        
        // Load fog configuration from map
        if (mapConfig.fog?.enabled) {
            this.fogConfig = {
                enabled: true,
                mode: mapConfig.fog.mode,
                density: mapConfig.fog.density,
                start: mapConfig.fog.start,
                end: mapConfig.fog.end,
                color: new Color3(mapConfig.fog.color.x, mapConfig.fog.color.y, mapConfig.fog.color.z)
            };
            logger.level.log(`🌫️ Fog config loaded: mode=${mapConfig.fog.mode}, density=${mapConfig.fog.density}`);
        } else {
            this.fogConfig = null;
            logger.level.debug(`🌫️ No fog config in map`);
        }
        
        // Apply settings to scene
        this.apply();
    }
    
    /**
     * Apply current settings to the scene (respects debug overrides)
     */
    public apply(): void {
        // Get the main hemispheric light
        const light = this.scene.getLightByName('light') as HemisphericLight;
        
        if (this.fullBrightMode) {
            // Full bright mode: 100% white ambient
            this.scene.ambientColor = Color3.White();
            this.scene.clearColor = new Color4(1, 1, 1, 1);
            if (light) {
                light.diffuse = Color3.White();
                light.groundColor = Color3.White();
                light.specular = Color3.White();
                light.intensity = 1.0;
            }
        } else {
            // Use map ambient color for both ambient light and background
            this.scene.ambientColor = this.mapAmbientColor;
            this.scene.clearColor = new Color4(
                this.mapAmbientColor.r,
                this.mapAmbientColor.g,
                this.mapAmbientColor.b,
                1
            );
            if (light) {
                light.diffuse = this.mapAmbientColor;
                light.groundColor = this.mapAmbientColor.scale(0.5); // Ground is darker
                light.specular = this.mapAmbientColor.scale(0.3);
                light.intensity = 1.0;
            }
        }
        // Apply fog settings
        if (this.fogConfig?.enabled && !this.fullBrightMode) {
            switch (this.fogConfig.mode) {
                case 'linear':
                    this.scene.fogMode = Scene.FOGMODE_LINEAR;
                    this.scene.fogStart = this.fogConfig.start;
                    this.scene.fogEnd = this.fogConfig.end;
                    break;
                case 'exp':
                    this.scene.fogMode = Scene.FOGMODE_EXP;
                    this.scene.fogDensity = this.fogConfig.density;
                    break;
                case 'exp2':
                    this.scene.fogMode = Scene.FOGMODE_EXP2;
                    this.scene.fogDensity = this.fogConfig.density;
                    break;
            }
            this.scene.fogColor = this.fogConfig.color;
            logger.level.debug(`🌫️ Fog applied: mode=${this.fogConfig.mode}, color=(${this.fogConfig.color.r.toFixed(2)}, ${this.fogConfig.color.g.toFixed(2)}, ${this.fogConfig.color.b.toFixed(2)})`);
        } else {
            this.scene.fogMode = Scene.FOGMODE_NONE;
        }
        
        logger.environment.debug('Environment applied:', this.fullBrightMode ? 'Full Bright' : `Map Ambient (${this.mapAmbientColor.r}, ${this.mapAmbientColor.g}, ${this.mapAmbientColor.b})`);
        logger.level.debug(`Environment applied: ${this.fullBrightMode ? 'Full Bright' : 'Map Ambient'}`);
    }
    
    /**
     * Toggle full bright debug mode
     */
    public setFullBrightMode(enabled: boolean): void {
        this.fullBrightMode = enabled;
        this.apply();
        logger.level.log(`💡 Full Bright mode: ${enabled ? 'ON' : 'OFF'}`);
    }
    
    /**
     * Get current full bright mode state
     */
    public getFullBrightMode(): boolean {
        return this.fullBrightMode;
    }
    
    /**
     * Get the map ambient color for debug UI display
     */
    public getMapAmbientColor(): Color3 {
        return this.mapAmbientColor.clone();
    }
    
    /**
     * Get the default camera configuration from map
     * Returns null if no camera config was loaded
     */
    public getDefaultCameraConfig(): CameraConfig | null {
        return this.defaultCameraConfig;
    }
    
    /**
     * Enable or disable fog at runtime
     */
    public setFogEnabled(enabled: boolean): void {
        if (!this.fogConfig) {
            // Initialize with defaults if no config exists
            // Use a gray-blue fog color instead of ambient (which might be black)
            this.fogConfig = {
                enabled,
                mode: 'exp2',
                density: 0.02,
                start: 10,
                end: 100,
                color: new Color3(0.3, 0.3, 0.4)  // Gray-blue fog, not black
            };
        } else {
            this.fogConfig.enabled = enabled;
        }
        this.apply();
        logger.level.log(`🌫️ Fog ${enabled ? 'enabled' : 'disabled'}`);
    }
    
    /**
     * Get current fog enabled state
     */
    public getFogEnabled(): boolean {
        return this.fogConfig?.enabled ?? false;
    }
    
    /**
     * Set fog density at runtime
     */
    public setFogDensity(density: number): void {
        if (this.fogConfig) {
            this.fogConfig.density = density;
            this.apply();
            logger.level.debug(`🌫️ Fog density set to ${density}`);
        }
    }
    
    /**
     * Get current fog density
     */
    public getFogDensity(): number {
        return this.fogConfig?.density ?? 0.02;
    }
    
    /**
     * Set fog color at runtime
     */
    public setFogColor(r: number, g: number, b: number): void {
        if (this.fogConfig) {
            this.fogConfig.color = new Color3(r, g, b);
            this.apply();
            logger.level.debug(`🌫️ Fog color set to (${r.toFixed(2)}, ${g.toFixed(2)}, ${b.toFixed(2)})`);
        }
    }
    
    /**
     * Get current fog color
     */
    public getFogColor(): Color3 {
        return this.fogConfig?.color?.clone() ?? this.mapAmbientColor.clone();
    }
    
    /**
     * Set ambient color at runtime
     */
    public setAmbientColor(r: number, g: number, b: number): void {
        this.mapAmbientColor = new Color3(r, g, b);
        this.apply();
        logger.level.log(`🌍 Ambient color set to (${r.toFixed(2)}, ${g.toFixed(2)}, ${b.toFixed(2)})`);
    }
}
